import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/apiService';
import {
  Plus, RefreshCw, RotateCcw, Save, CheckCircle, XCircle,
  Shield, Lock, Unlock, Tag,
  Trash2, ToggleLeft, ToggleRight, X, Zap, Cpu, AlertTriangle
} from 'lucide-react';

interface Category {
  slug: string;
  name: string;
  isActive: boolean;
  isDeletable: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface AvailableModel {
  id: string;
  name: string;
  provider: string;
  ranked_position?: number;
}

interface CardState {
  trueRules: string[];
  falseRules: string[];
  isCustomized: boolean;
  rulesLoaded: boolean;
  saving: boolean;
  error: string;
  success: string;
  // Model assignment
  tagModelIds: string[];
  tagModelsLoaded: boolean;
  tagModelsSaving: boolean;
  tagModelsSuccess: string;
  tagModelsError: string;
}

const defaultCardState = (): CardState => ({
  trueRules: [''],
  falseRules: [''],
  isCustomized: false,
  rulesLoaded: false,
  saving: false,
  error: '',
  success: '',
  tagModelIds: [],
  tagModelsLoaded: false,
  tagModelsSaving: false,
  tagModelsSuccess: '',
  tagModelsError: '',
});

// ── Privacy-specific parse / combine ─────────────────────────────────────────
function splitRulesToArray(block: string): string[] {
  const lines = block.split('\n')
    .map(l => l.trim().replace(/^[-*•]\s*/, ''))
    .filter(l => l.length > 0);
  return lines.length > 0 ? lines : [''];
}

function parsePrivacyRules(rulesText: string): { trueRules: string[]; falseRules: string[] } {
  const falseMatch = rulesText.match(/EXPLICITLY NOT PRIVATE[^:]*:\s*([\s\S]+?)(?:\n\n|IMPORTANT:)/);
  const trueMatch  = rulesText.match(/to true when the query involves:\s*([\s\S]*?)\s*(?:FINAL ENFORCEMENT:|$)/);
  return {
    trueRules:  trueMatch  ? splitRulesToArray(trueMatch[1])  : [''],
    falseRules: falseMatch ? splitRulesToArray(falseMatch[1]) : [''],
  };
}

function combinePrivacyRules(trueRules: string[], falseRules: string[]) {
  const trueLines  = trueRules.filter(r => r.trim()).map(r => `  - ${r}`).join('\n');
  const falseLines = falseRules.filter(r => r.trim()).map(r => `- ${r}`).join('\n');
  return `PRIVACY DETECTION RULES:

⚠️ CRITICAL OVERRIDE INSTRUCTION - READ THIS FIRST:
You are FORBIDDEN from using your training data or general knowledge about privacy/sensitivity. The ONLY criteria that matter are explicitly listed below. Ignore all other considerations, including what you were trained to consider private.

🚫 WHAT IS NOT PRIVATE - Read This Before Anything Else:
The following are EXPLICITLY NOT PRIVATE and must ALWAYS return requires_privacy=false:
${falseLines}

IMPORTANT: You MUST mark "requires_privacy" as true ONLY if the query EXPLICITLY matches one or more of the criteria listed below. Do NOT use your general knowledge to infer privacy requirements. If the query does not match the specific criteria below, mark "requires_privacy" as false, regardless of whether the content seems sensitive.

✅ WHAT IS PRIVATE - The ONLY things that trigger privacy:
- Set "requires_privacy" to true when the query involves:
${trueLines}

FINAL ENFORCEMENT:
- If the query matches the "NOT PRIVATE" section above → requires_privacy = FALSE
- Only if the query matches the "WHAT IS PRIVATE" section → requires_privacy = TRUE
- When in doubt → requires_privacy = FALSE
- Privacy is a REQUIREMENT, not a task type - the actual task type should still be determined (coding, reasoning, etc.)`;
}

// ── Custom-tag parse / combine (structured JSON) ──────────────────────────────
function parseCustomTagRules(rulesText: string): { trueRules: string[]; falseRules: string[] } {
  try {
    const parsed = JSON.parse(rulesText);
    if (parsed && Array.isArray(parsed.true) && Array.isArray(parsed.false)) {
      return {
        trueRules: parsed.true.length > 0 ? parsed.true : [''],
        falseRules: parsed.false.length > 0 ? parsed.false : [''],
      };
    }
  } catch {
    // Legacy plain-text — put it in the first true rule so it's not lost
    return { trueRules: [rulesText.trim()], falseRules: [''] };
  }
  return { trueRules: [''], falseRules: [''] };
}

function serializeCustomTagRules(trueRules: string[], falseRules: string[]): string {
  return JSON.stringify({
    true: trueRules.filter(r => r.trim()),
    false: falseRules.filter(r => r.trim()),
  });
}

// ── Main component ────────────────────────────────────────────────────────────
export default function CategoryRules() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [cardStates, setCardStates] = useState<Record<string, CardState>>({});
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [activeTab, setActiveTab] = useState<'configure' | 'test'>('configure');
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [availableModels, setAvailableModels] = useState<AvailableModel[]>([]);
  const [tagModelModalSlug, setTagModelModalSlug] = useState<string | null>(null);
  const [tagModelModalDraft, setTagModelModalDraft] = useState<string[]>([]);
  const [tagModelModalSearch, setTagModelModalSearch] = useState('');

  // Load available models for tag assignment
  useEffect(() => {
    apiService.getAvailableModelsForList().then((data: any) => {
      const models = (data?.data || data || [])
        .map((m: any) => ({ id: m.id, name: m.name, provider: m.provider, ranked_position: m.ranked_position }))
        .sort((a: AvailableModel, b: AvailableModel) => (a.ranked_position || 999) - (b.ranked_position || 999));
      setAvailableModels(models);
    }).catch(() => {});
  }, []);

  // Test center
  const [centerQuery, setCenterQuery] = useState('');
  const [centerResult, setCenterResult] = useState<any>(null);
  const [centerTesting, setCenterTesting] = useState(false);
  const [centerError, setCenterError] = useState('');
  const [tdModel, setTdModel] = useState<{ name: string; rank: number } | null>(null);

  useEffect(() => {
    apiService.get('/portal/models/custom').then((models: any[]) => {
      const privacyTd = models
        .filter((m: any) => m.taskDeterminationModel === true && m.isPrivacyCompliant === true)
        .sort((a: any, b: any) => (a.ranked_position || 999) - (b.ranked_position || 999));
      if (privacyTd.length > 0) {
        setTdModel({ name: privacyTd[0].name, rank: privacyTd[0].ranked_position || 999 });
      }
    }).catch(() => {});
  }, []);

  const handleCenterTest = async () => {
    if (!centerQuery.trim()) return;
    setCenterTesting(true);
    setCenterError('');
    setCenterResult(null);
    try {
      const data = await apiService.testAllCategoryRules(centerQuery);
      if (data.success) {
        setCenterResult(data.result);
      } else {
        setCenterError(data.message || 'Test failed');
      }
    } catch (err: any) {
      setCenterError(err.response?.data?.message || 'Failed to run test');
    } finally {
      setCenterTesting(false);
    }
  };

  // Add modal
  const [categorySearch, setCategorySearch] = useState('');

  // Add modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [addError, setAddError] = useState('');
  const [adding, setAdding] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      setPageLoading(true);
      setPageError('');
      const data = await apiService.listCategories();
      const cats: Category[] = (data.categories || []).filter((c: Category) => c.slug && c.name);
      setCategories(cats);
      setCardStates(prev => {
        const next = { ...prev };
        cats.forEach(c => { if (!next[c.slug]) next[c.slug] = defaultCardState(); });
        return next;
      });
    } catch (err: any) {
      setPageError(err.response?.data?.message || 'Failed to load categories');
    } finally {
      setPageLoading(false);
    }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  // Auto-select first category once loaded
  useEffect(() => {
    if (!selectedSlug && categories.length > 0) {
      const first = categories[0];
      setSelectedSlug(first.slug);
      loadRules(first.slug);
      loadTagModels(first.slug);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  const updateCard = (slug: string, patch: Partial<CardState>) =>
    setCardStates(prev => ({ ...prev, [slug]: { ...prev[slug], ...patch } }));

  const loadRules = async (slug: string, force = false) => {
    const cs = cardStates[slug];
    if (!force && cs?.rulesLoaded) return;
    try {
      const data = await apiService.getCategoryRules(slug);
      if (data.success) {
        if (slug === 'privacy') {
          const { trueRules, falseRules } = parsePrivacyRules(data.rules);
          updateCard(slug, { trueRules, falseRules, isCustomized: data.isCustomized ?? false, rulesLoaded: true });
        } else {
          const { trueRules, falseRules } = parseCustomTagRules(data.rules);
          updateCard(slug, { trueRules, falseRules, isCustomized: data.isCustomized ?? false, rulesLoaded: true });
        }
      }
    } catch {
      updateCard(slug, { error: 'Failed to load rules', rulesLoaded: true });
    }
  };

  const loadTagModels = async (slug: string, force = false) => {
    if (slug === 'privacy') return;
    const cs = cardStates[slug];
    if (!force && cs?.tagModelsLoaded) return;
    try {
      const data = await apiService.getTagModels(slug);
      const modelIds = (data as any)?.data?.modelIds || (data as any)?.modelIds || [];
      updateCard(slug, { tagModelIds: modelIds, tagModelsLoaded: true });
    } catch {
      updateCard(slug, { tagModelIds: [], tagModelsLoaded: true });
    }
  };

  const handleSelectCategory = async (slug: string) => {
    setSelectedSlug(slug);
    await loadRules(slug);
    await loadTagModels(slug);
  };

  const openTagModelModal = (cat: Category) => {
    const cs = cardStates[cat.slug];
    setTagModelModalDraft(cs?.tagModelIds ? [...cs.tagModelIds] : []);
    setTagModelModalSearch('');
    setTagModelModalSlug(cat.slug);
  };

  const handleSaveTagModels = async () => {
    if (!tagModelModalSlug) return;
    updateCard(tagModelModalSlug, { tagModelsSaving: true, tagModelsError: '', tagModelsSuccess: '' });
    try {
      await apiService.putTagModels(tagModelModalSlug, tagModelModalDraft);
      updateCard(tagModelModalSlug, { tagModelIds: tagModelModalDraft, tagModelsSaving: false, tagModelsSuccess: 'Model assignments saved!' });
      setTagModelModalSlug(null);
      setTimeout(() => updateCard(tagModelModalSlug!, { tagModelsSuccess: '' }), 3000);
    } catch (err: any) {
      updateCard(tagModelModalSlug, { tagModelsSaving: false, tagModelsError: err.response?.data?.message || 'Failed to save model assignments' });
    }
  };

  const handleToggleActive = async (cat: Category) => {
    const nextActive = !cat.isActive;
    try {
      await apiService.toggleCategoryActive(cat.slug, nextActive);
      setCategories(prev => prev.map(c => c.slug === cat.slug ? { ...c, isActive: nextActive } : c));
    } catch (err: any) {
      setPageError(err.response?.data?.message || 'Failed to update category');
    }
  };

  const handleSaveRules = async (cat: Category) => {
    const cs = cardStates[cat.slug];
    updateCard(cat.slug, { saving: true, error: '', success: '' });
    try {
      const combined =
        cat.slug === 'privacy'
          ? combinePrivacyRules(cs.trueRules, cs.falseRules)
          : serializeCustomTagRules(cs.trueRules, cs.falseRules);
      const data = await apiService.updateCategoryRules(cat.slug, combined);
      if (data.success) {
        updateCard(cat.slug, { success: 'Rules saved!', isCustomized: true, saving: false });
        setTimeout(() => updateCard(cat.slug, { success: '' }), 3000);
      }
    } catch (err: any) {
      updateCard(cat.slug, { error: err.response?.data?.message || 'Failed to save rules', saving: false });
    }
  };

  const handleResetToDefault = async (slug: string) => {
    if (!window.confirm('Reset to default rules? This cannot be undone.')) return;
    updateCard(slug, { saving: true, error: '', success: '' });
    try {
      await apiService.resetCategoryToDefault(slug);
      updateCard(slug, { saving: false, rulesLoaded: false });
      await loadRules(slug, true);
      updateCard(slug, { success: 'Reset to default!', isCustomized: false });
      setTimeout(() => updateCard(slug, { success: '' }), 3000);
    } catch (err: any) {
      updateCard(slug, { error: err.response?.data?.message || 'Failed to reset', saving: false });
    }
  };

  const handleDeleteCategory = async (slug: string) => {
    if (!window.confirm(`Delete the "${slug}" category? This cannot be undone.\n\nAny API keys using this tag will have it automatically removed.`)) return;
    try {
      await apiService.deleteCategory(slug);
      setCategories(prev => prev.filter(c => c.slug !== slug));
      setCardStates(prev => { const n = { ...prev }; delete n[slug]; return n; });
      if (selectedSlug === slug) setSelectedSlug(null);
    } catch (err: any) {
      setPageError(err.response?.data?.message || 'Failed to delete category');
    }
  };

  // Add modal helpers
  const autoSlug = (name: string) =>
    name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 32);

  const handleNameChange = (val: string) => {
    setNewName(val);
    setNewSlug(autoSlug(val));
  };

  const handleAddCategory = async () => {
    if (!newName.trim()) { setAddError('Tag name is required'); return; }
    setAdding(true);
    setAddError('');
    try {
      const createdSlug = newSlug.trim();
      await apiService.createCategory(newName.trim(), createdSlug);
      setAddModalOpen(false);
      setNewName('');
      setNewSlug('');
      await loadCategories();
      setSelectedSlug(createdSlug);
      await loadRules(createdSlug);
      await loadTagModels(createdSlug);
    } catch (err: any) {
      setAddError(err.response?.data?.message || 'Failed to create category');
    } finally {
      setAdding(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  const selectedCat = categories.find(c => c.slug === selectedSlug) ?? null;
  const selectedState = selectedSlug ? (cardStates[selectedSlug] || defaultCardState()) : null;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Shield className="h-8 w-8" />
              <h1 className="text-3xl font-bold">Category Rules</h1>
            </div>
            <p className="text-indigo-100 text-sm">
              Manage detection rules for Privacy and your custom tags. Active categories are evaluated on every request.
            </p>
          </div>
          <button
            onClick={loadCategories}
            className="inline-flex items-center px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-sm font-medium text-white hover:bg-white/20 transition-all"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Reload
          </button>
        </div>
      </div>

      {pageError && (
        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-r flex items-start gap-3">
          <XCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div>{pageError}</div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('configure')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
            activeTab === 'configure'
              ? 'bg-indigo-600 text-white shadow-sm'
              : 'bg-white border border-gray-200 text-gray-500 hover:border-indigo-300 hover:text-indigo-600'
          }`}
        >
          <Shield className="h-4 w-4" /> Configure Rules
        </button>
        <button
          onClick={() => setActiveTab('test')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
            activeTab === 'test'
              ? 'bg-indigo-600 text-white shadow-sm'
              : 'bg-white border border-gray-200 text-gray-500 hover:border-indigo-300 hover:text-indigo-600'
          }`}
        >
          <Zap className="h-4 w-4" /> Test Center
        </button>
      </div>

      {/* Tab content */}
      <div style={{ minHeight: '620px' }}>
      {activeTab === 'configure' && (
        <div className="flex gap-6" style={{ minHeight: '560px' }}>
          {/* Sidebar */}
          <div className="w-56 flex-shrink-0 flex flex-col gap-2">
            <input
              type="text"
              placeholder="Search tags..."
              value={categorySearch}
              onChange={e => setCategorySearch(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
            />
            {categories.filter(c =>
              !categorySearch.trim() || c.name.toLowerCase().includes(categorySearch.toLowerCase()) || c.slug.toLowerCase().includes(categorySearch.toLowerCase())
            ).map(cat => (
              <div
                key={cat.slug}
                role="button"
                tabIndex={0}
                onClick={() => handleSelectCategory(cat.slug)}
                onKeyDown={e => e.key === 'Enter' && handleSelectCategory(cat.slug)}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all group cursor-pointer ${
                  selectedSlug === cat.slug
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    {cat.slug === 'privacy'
                      ? <Shield className={`h-4 w-4 flex-shrink-0 ${selectedSlug === cat.slug ? 'text-indigo-600' : 'text-gray-400'}`} />
                      : <Tag className={`h-4 w-4 flex-shrink-0 ${selectedSlug === cat.slug ? 'text-indigo-600' : 'text-gray-400'}`} />
                    }
                    <span className={`text-sm font-semibold truncate ${selectedSlug === cat.slug ? 'text-indigo-700' : 'text-gray-700'}`}>
                      {cat.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 ml-1 flex-shrink-0">
                    {cat.isDeletable && (
                      <button
                        onClick={e => { e.stopPropagation(); handleDeleteCategory(cat.slug); }}
                        className="opacity-0 group-hover:opacity-100 p-0.5 text-red-400 hover:text-red-600 transition-all"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <span className={`w-2 h-2 rounded-full ${(cat.isActive || cat.slug === 'privacy') ? 'bg-green-400' : 'bg-gray-300'}`} />
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={() => setAddModalOpen(true)}
              className="w-full mt-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-gray-300 text-sm font-medium text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition-all flex items-center justify-center gap-2"
            >
              <Plus className="h-4 w-4" /> Add Custom Tag
            </button>
          </div>

          {/* Editor Panel */}
          <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {!selectedCat || !selectedState ? (
              <div className="flex items-center justify-center h-full text-gray-300">
                <div className="text-center">
                  <Tag className="h-12 w-12 mx-auto mb-3 opacity-40" />
                  <p className="text-sm text-gray-400">Select a category to edit its rules</p>
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col">
                {/* Editor header */}
                <div className={`px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r ${
                  selectedCat.slug === 'privacy' ? 'from-indigo-50 to-purple-50' : 'from-violet-50 to-fuchsia-50'
                }`}>
                  <div className="flex items-center gap-3">
                    {selectedCat.slug === 'privacy'
                      ? <Shield className="h-5 w-5 text-indigo-600" />
                      : <Tag className="h-5 w-5 text-violet-600" />
                    }
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="font-semibold text-gray-900">{selectedCat.name}</h2>
                        {selectedState.isCustomized && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Customized</span>
                        )}
                        {!selectedState.isCustomized && selectedState.rulesLoaded && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Default</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {selectedCat.slug === 'privacy' ? 'Built-in · Cannot delete' : `slug: ${selectedCat.slug}`}
                      </p>
                    </div>
                  </div>
                  {selectedCat.slug === 'privacy' ? (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 cursor-default" title="Privacy detection is always active">
                      <ToggleRight className="h-4 w-4" /> Always Active
                    </span>
                  ) : (
                    <button
                      onClick={() => handleToggleActive(selectedCat)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                        selectedCat.isActive
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {selectedCat.isActive ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                      {selectedCat.isActive ? 'Active' : 'Disabled'}
                    </button>
                  )}
                </div>

                {/* Editor body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                  {selectedState.error && (
                    <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-r flex items-start gap-3">
                      <XCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 text-sm">{selectedState.error}</div>
                    </div>
                  )}
                  {selectedState.success && (
                    <div className="bg-green-50 border-l-4 border-green-500 text-green-700 px-4 py-3 rounded-r flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 text-sm">{selectedState.success}</div>
                    </div>
                  )}

                  {!selectedState.rulesLoaded ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500" />
                    </div>
                  ) : (
                    <>
                      {/* TRUE section */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Lock className="h-4 w-4 text-amber-600" />
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300">
                            Set {selectedCat.slug} = true when
                          </span>
                        </div>
                        <div className="space-y-2">
                          {selectedState.trueRules.map((rule, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <span className="text-gray-400 text-sm font-mono flex-shrink-0">*</span>
                              <input
                                type="text"
                                value={rule}
                                onChange={e => {
                                  const next = [...selectedState.trueRules];
                                  next[i] = e.target.value;
                                  updateCard(selectedCat.slug, { trueRules: next });
                                }}
                                className="flex-1 px-3 py-2 border-2 border-amber-200 rounded-lg text-sm bg-amber-50/50 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all"
                                placeholder={`e.g. query involves ${selectedCat.name.toLowerCase()}-related content`}
                              />
                              {selectedState.trueRules.length > 1 && (
                                <button
                                  onClick={() => updateCard(selectedCat.slug, { trueRules: selectedState.trueRules.filter((_, j) => j !== i) })}
                                  className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
                                  title="Remove"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            onClick={() => updateCard(selectedCat.slug, { trueRules: [...selectedState.trueRules, ''] })}
                            className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium transition-colors"
                          >
                            <Plus className="h-3.5 w-3.5" /> Add condition
                          </button>
                          {selectedState.trueRules.every(r => !r.trim()) && (
                            <p className="text-xs text-gray-400 italic mt-1">
                              No criteria set — this tag won't detect any queries until you add at least one condition.
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Exclusions</span>
                        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
                      </div>

                      {/* FALSE section */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Unlock className="h-4 w-4 text-green-600" />
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800 border border-green-300">
                            Set {selectedCat.slug} = false when
                          </span>
                        </div>
                        <div className="space-y-2">
                          {selectedState.falseRules.map((rule, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <span className="text-gray-400 text-sm font-mono flex-shrink-0">*</span>
                              <input
                                type="text"
                                value={rule}
                                onChange={e => {
                                  const next = [...selectedState.falseRules];
                                  next[i] = e.target.value;
                                  updateCard(selectedCat.slug, { falseRules: next });
                                }}
                                className="flex-1 px-3 py-2 border-2 border-green-200 rounded-lg text-sm bg-green-50/50 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
                                placeholder="e.g. general questions unrelated to this topic"
                              />
                              {selectedState.falseRules.length > 1 && (
                                <button
                                  onClick={() => updateCard(selectedCat.slug, { falseRules: selectedState.falseRules.filter((_, j) => j !== i) })}
                                  className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
                                  title="Remove"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            onClick={() => updateCard(selectedCat.slug, { falseRules: [...selectedState.falseRules, ''] })}
                            className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-medium transition-colors"
                          >
                            <Plus className="h-3.5 w-3.5" /> Add condition
                          </button>
                        </div>
                      </div>

                      {/* Model Assignment (custom tags only) — compact button row */}
                      {selectedCat.slug !== 'privacy' && (
                        <div className="flex items-center justify-between pt-1 pb-1 border-t border-gray-100">
                          <div className="flex items-center gap-2">
                            <Cpu className="h-4 w-4 text-indigo-400" />
                            <span className="text-xs text-gray-500">
                              {!selectedState.tagModelsLoaded ? (
                                <span className="italic">Loading models…</span>
                              ) : selectedState.tagModelIds.length === 0 ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                  <AlertTriangle className="h-3 w-3" /> No models assigned
                                </span>
                              ) : (
                                <span className="font-medium text-indigo-700">
                                  {selectedState.tagModelIds.length} model{selectedState.tagModelIds.length !== 1 ? 's' : ''} assigned
                                </span>
                              )}
                            </span>
                            {selectedState.tagModelsSuccess && (
                              <span className="text-xs text-green-600 font-medium">{selectedState.tagModelsSuccess}</span>
                            )}
                          </div>
                          <button
                            onClick={() => openTagModelModal(selectedCat)}
                            disabled={!selectedState.tagModelsLoaded}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border-2 border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-40 transition-all"
                          >
                            <Cpu className="h-3.5 w-3.5" /> Assign Models
                          </button>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                        <div>
                          {selectedCat.slug === 'privacy' && (
                            <button
                              onClick={() => handleResetToDefault(selectedCat.slug)}
                              disabled={selectedState.saving}
                              className="inline-flex items-center px-4 py-2 border-2 border-gray-300 text-sm font-semibold rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-all"
                            >
                              <RotateCcw className="h-4 w-4 mr-2" /> Restore to Default
                            </button>
                          )}
                        </div>
                        <button
                          onClick={() => handleSaveRules(selectedCat)}
                          disabled={selectedState.saving || selectedState.trueRules.every(r => !r.trim())}
                          className="inline-flex items-center px-5 py-2 border border-transparent text-sm font-semibold rounded-lg text-white bg-indigo-600 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 shadow transition-all"
                        >
                          <Save className="h-4 w-4 mr-2" />
                          {selectedState.saving ? 'Saving...' : 'Save Rules'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Test Tab */}
      {activeTab === 'test' && (
        <TestCenter
          categories={categories}
          query={centerQuery}
          onQueryChange={setCenterQuery}
          onTest={handleCenterTest}
          testing={centerTesting}
          result={centerResult}
          error={centerError}
          tdModel={tdModel}
        />
      )}
      </div>

      {/* Add Custom Tag Modal */}
      {addModalOpen && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] !m-0" onClick={() => { setAddModalOpen(false); setAddError(''); setNewName(''); setNewSlug(''); }}>
          <div className="bg-white rounded-xl shadow-2xl w-[440px] p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Custom Tag</h3>
              <button onClick={() => { setAddModalOpen(false); setAddError(''); setNewName(''); setNewSlug(''); }}>
                <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            </div>

            {addError && (
              <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{addError}</div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tag Name *</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g. Marketing"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
                />
              </div>
              <p className={`text-xs -mt-1 min-h-[1rem] ${categories.some(c => c.slug === newSlug) ? 'text-red-500' : 'text-gray-400'}`}>
                {newSlug ? <>Identifier: <span className="font-mono">{newSlug}</span>{categories.some(c => c.slug === newSlug) && ' — already exists'}</> : ''}
              </p>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => { setAddModalOpen(false); setAddError(''); setNewName(''); setNewSlug(''); }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCategory}
                disabled={adding || !newName.trim() || categories.some(c => c.slug === newSlug)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
              >
                {adding ? 'Creating...' : 'Create Tag'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Assign Models Modal */}
      {tagModelModalSlug && (() => {
        const modalCat = categories.find(c => c.slug === tagModelModalSlug);
        const cs = cardStates[tagModelModalSlug];
        const filtered = availableModels.filter(m =>
          !tagModelModalSearch.trim() ||
          m.name.toLowerCase().includes(tagModelModalSearch.toLowerCase()) ||
          m.provider.toLowerCase().includes(tagModelModalSearch.toLowerCase())
        );
        return (
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] !m-0" onClick={() => setTagModelModalSlug(null)}>
            <div className="bg-white rounded-xl shadow-2xl w-[520px] flex flex-col" style={{ maxHeight: '80vh' }} onClick={e => e.stopPropagation()}>
              {/* Modal header */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Cpu className="h-4 w-4 text-indigo-500" />
                    Assign Models — <span className="font-mono text-indigo-600">{tagModelModalSlug}</span>
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Selected models will be used when this tag fires. Click to toggle.
                  </p>
                </div>
                <button onClick={() => setTagModelModalSlug(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Search */}
              <div className="px-6 py-3 border-b border-gray-100 flex-shrink-0">
                <input
                  type="text"
                  value={tagModelModalSearch}
                  onChange={e => setTagModelModalSearch(e.target.value)}
                  placeholder="Search models…"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
                  autoFocus
                />
              </div>

              {/* Model list */}
              <div className="flex-1 overflow-y-auto">
                {filtered.length === 0 ? (
                  <p className="text-sm text-gray-400 italic text-center py-10">No models match your search</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="w-8 px-4 py-2.5" />
                        <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                        <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Provider</th>
                        <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Rank</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filtered.map(m => {
                        const selected = tagModelModalDraft.includes(m.id);
                        return (
                          <tr
                            key={m.id}
                            onClick={() => setTagModelModalDraft(prev =>
                              selected ? prev.filter(id => id !== m.id) : [...prev, m.id]
                            )}
                            className={`cursor-pointer transition-colors ${
                              selected ? 'bg-indigo-50 hover:bg-indigo-100' : 'hover:bg-gray-50'
                            }`}
                          >
                            <td className="px-4 py-2.5">
                              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                                selected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'
                              }`}>
                                {selected && <CheckCircle className="w-3 h-3 text-white" />}
                              </div>
                            </td>
                            <td className={`px-3 py-2.5 font-medium ${selected ? 'text-indigo-800' : 'text-gray-800'}`}>
                              {m.name}
                            </td>
                            <td className="px-3 py-2.5 text-xs text-gray-400">{m.provider}</td>
                            <td className="px-3 py-2.5 text-xs text-gray-400">{m.ranked_position || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
                <span className="text-xs text-gray-400">
                  {tagModelModalDraft.length === 0
                    ? 'None selected — tag fires but pool unrestricted'
                    : `${tagModelModalDraft.length} model${tagModelModalDraft.length !== 1 ? 's' : ''} selected`}
                </span>
                <div className="flex gap-3">
                  <button
                    onClick={() => setTagModelModalSlug(null)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveTagModels}
                    disabled={cs?.tagModelsSaving}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <Save className="h-3.5 w-3.5" />
                    {cs?.tagModelsSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Test Center ───────────────────────────────────────────────────────────────
interface TestCenterProps {
  categories: Category[];
  query: string;
  onQueryChange: (v: string) => void;
  onTest: () => void;
  testing: boolean;
  result: any;
  error: string;
  tdModel: { name: string; rank: number } | null;
}

const QUICK_EXAMPLES = [
  { label: '💊 Medical Info', query: 'I have diabetes and take insulin daily' },
  { label: '📣 Marketing Brief', query: 'Write a marketing campaign for our Q4 product launch' },
  { label: '⚖️ Legal Review', query: 'Review this NDA clause for compliance issues' },
  { label: '❓ General Question', query: 'What is the capital of France?' },
  { label: '🔒 Business Confidential', query: "Our company's Q4 earnings were $5M, keep it confidential" },
];

function TestCenter({ categories, query, onQueryChange, onTest, testing, result, error, tdModel }: TestCenterProps) {
  const activeCategories = categories.filter(c => c.isActive);

  return (
    <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-100">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200 px-6 py-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Zap className="h-5 w-5 text-blue-600" /> Test Center
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Run one query against all active categories at once — mirrors real-world task determination behaviour.
            </p>
            <p className="text-xs text-gray-400 mt-2">
              {tdModel
                ? <>The highest-ranked privacy-compliant TD model is used. Detection accuracy varies by model capability — if results seem off, switch to a more capable TD model (e.g. GPT-4o).</>
                : <>No privacy-compliant TD model configured. Add one in Models to enable testing.</>
              }
            </p>
          </div>
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border flex-shrink-0 ml-6 ${tdModel ? 'bg-white border-blue-200 text-blue-800' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
            {tdModel ? (
              <>
                <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
                <span>TD model: <span className="font-semibold">{tdModel.name}</span></span>
                {tdModel.rank < 999 && <span className="text-blue-400 font-mono ml-1">rank {tdModel.rank}</span>}
              </>
            ) : (
              <>
                <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                <span>No privacy TD model configured</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Quick Examples</p>
          <div className="flex flex-wrap gap-2">
            {QUICK_EXAMPLES.map(ex => (
              <button
                key={ex.label}
                onClick={() => onQueryChange(ex.query)}
                className="px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 border border-blue-200 transition-all"
              >
                {ex.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <input
            type="text"
            value={query}
            onChange={e => onQueryChange(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && query.trim() && onTest()}
            placeholder="Enter a query to test all active categories…"
            className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all hover:border-gray-300"
          />
          <button
            onClick={onTest}
            disabled={testing || !query.trim()}
            className="inline-flex items-center px-6 py-3 bg-blue-600 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 shadow transition-all"
          >
            <Zap className="h-4 w-4 mr-2" />
            {testing ? 'Running…' : 'Run Test'}
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-r flex items-start gap-3 text-sm">
            <XCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {testing && (
          <div className="flex items-center justify-center py-10 text-gray-400">
            <div className="animate-spin rounded-full h-10 w-10 border-b-4 border-indigo-600 mr-3" />
            <span className="text-sm font-medium">Running task determination…</span>
          </div>
        )}

        {result && !testing && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500 font-medium">Task Type</p>
                <p className="text-sm font-bold text-gray-900 capitalize mt-0.5">{result.taskType}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500 font-medium">Complexity</p>
                <p className="text-sm font-bold text-gray-900 mt-0.5">{result.complexity} / 5</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500 font-medium">Model Used</p>
                <p className="text-sm font-bold text-gray-900 truncate mt-0.5">{result.modelUsed || '—'}</p>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Category Results — {activeCategories.length} active
              </p>
              {activeCategories.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No active categories to display.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {activeCategories.some(c => c.slug === 'privacy') && (
                    <CategoryResultBadge name="Privacy" slug="privacy" fired={result.requiresPrivacy === true} />
                  )}
                  {activeCategories.filter(c => c.slug !== 'privacy').map(cat => (
                    <CategoryResultBadge key={cat.slug} name={cat.name} slug={cat.slug} fired={result.customTags?.[cat.slug] === true} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {!result && !testing && !error && (
          <div className="flex items-center justify-center py-10 border-2 border-dashed border-gray-200 rounded-lg">
            <div className="text-center">
              <Zap className="h-10 w-10 mx-auto mb-2 opacity-40 text-gray-300" />
              <p className="text-sm font-medium text-gray-400">No results yet</p>
              <p className="text-xs text-gray-400 mt-0.5">Enter a query above to see how all categories respond</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryResultBadge({ name, slug, fired }: { name: string; slug: string; fired: boolean }) {
  return (
    <div className={`flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all ${fired ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-200'}`}>
      <div className="flex items-center gap-2">
        {slug === 'privacy'
          ? <Shield className={`h-4 w-4 ${fired ? 'text-green-600' : 'text-amber-600'}`} />
          : <Tag className={`h-4 w-4 ${fired ? 'text-green-600' : 'text-amber-600'}`} />
        }
        <div>
          <span className="text-sm font-semibold text-gray-800">{name}</span>
          <span className="ml-1.5 text-xs text-gray-400 font-mono">{slug}</span>
        </div>
      </div>
      <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${fired ? 'bg-green-200 text-green-800' : 'bg-amber-200 text-amber-800'}`}>
        {fired ? <><CheckCircle className="h-3 w-3" /> true</> : <><Lock className="h-3 w-3" /> false</>}
      </div>
    </div>
  );
}
